import { extend } from 'vee-validate';
import { auPostcodeMatchesState, isValidPostcode } from './postcode';
/* eslint-disable */
import {
  required,
  required_if,
  email,
  min_value,
  max_value,
  max,
  numeric,
  regex,
  confirmed,
  ext,
  size as fileSize,
  alpha,
} from 'vee-validate/dist/rules';
/* eslint-enable */

export default () => {
  extend('required_with', {
    validate(value, { target }) {
      return value ? !!target : true;
    },
    params: ['target', 'otherField'],
    message: 'The {otherField} field is required when the {_field_} field has value',
  });
  extend('required_if', required_if);
  extend('email', email);
  extend('min_value', {
    // eslint-disable-next-line camelcase
    ...min_value,
    message: '{_field_} must be greater than {min}.',
  });
  extend('maxValue', {
    validate(value, { maxValue }) {
      return value <= Number(maxValue);
    },
    params: ['maxValue'],
    message: 'The {_field_} must be lower than  {maxValue} ',
  });
  extend('halfValue', {
    validate(value) {
      return value % 0.5 === 0;
    },
    message: 'Please enter a valid PD value',
  });
  extend('minLength', {
    validate(value, { minValue }) {
      return value.length >= minValue;
    },
    params: ['minValue'],
    message: 'The {_field_} field has to be at least {minValue} characters',
  });
  extend('maxLength', {
    validate(value, { maxValue }) {
      return value.length <= maxValue;
    },
    params: ['maxValue'],
    message: 'The {_field_} field can be maximum {maxValue} characters',
  });
  extend('between', {
    validate(value, { minVal, max }) {
      const valueNumber = Number(value);
      return Number(minVal) <= valueNumber && valueNumber <= Number(max);
    },
    params: ['minVal', 'max'],
    message: 'The {_field_} field has to be between {minVal} and {max}',
  });
  extend('maxSize', {
    validate(files, { size }) {
      if (Number.isNaN(size)) {
        return false;
      }

      const nSize = size * 1024 * 1024;
      if (!Array.isArray(files)) {
        return files.size <= nSize;
      }

      for (let i = 0; i < files.length; i += 1) {
        if (files[i].size > nSize) {
          return false;
        }
      }

      return true;
    },
    params: ['size'],
    message: 'The {_field_} field has to be smaller than {size}MB',
  });
  extend('required', {
    ...required,
    message: 'The {_field_} field is required.',
  });
  extend('regex', regex);
  extend('confirmed', confirmed);
  extend('ext', ext);
  extend('fileSize', fileSize);
  extend('no_mustache', text => {
    if (!text.match(/{{\s?([^}]*)\s?}}/g)) {
      return true;
    }
    return 'Message has unresolved variable(s).';
  });
  extend('alpha_spaces', {
    validate: value => /^[A-Za-z\s]*$/.test(value),
    message: 'The {_field_} field may only contain alphabetic characters and spaces'
  });
  extend('max', max);
  extend('numeric', numeric);
  extend('min_value', min_value);
  extend('max_value', max_value);
  extend('alpha', alpha);
  
  // Australian state validation - only valid Australian state codes
  extend('au_state', {
    validate: value => {
      if (!value) return false;
      
      // Valid Australian state and territory codes
      const validStates = [
        'NSW', // New South Wales
        'VIC', // Victoria
        'QLD', // Queensland
        'WA',  // Western Australia
        'SA',  // South Australia
        'TAS', // Tasmania
        'ACT', // Australian Capital Territory
        'NT'   // Northern Territory
      ];
      
      // Check if the value matches any valid state code (case insensitive)
      return validStates.includes(value.toUpperCase());
    },
    message: 'Please enter a valid Australian state abbreviation (NSW, VIC, QLD, WA, SA, TAS, ACT, NT)'
  });
  
  // Australian postcode validation - real Australia Post range, and matching
  // the state's ranges when one is selected (DP-4577)
  extend('au_postcode', {
    params: ['state'],
    validate: (value, { state }) => {
      if (!value || !isValidPostcode(value, 'AU')) {
        return 'Please enter a valid 4-digit Australian postcode';
      }
      if (!auPostcodeMatchesState(value, state)) {
        return 'Postcode does not match the selected state';
      }
      return true;
    }
  });

  // Canadian postal code validation - Canada Post "A1A 1A1" format (DP-4577)
  extend('ca_postcode', {
    validate: value => {
      if (!value) return false;
      return isValidPostcode(value, 'CA');
    },
    message: 'Please enter a valid Canadian postal code (e.g. M5V 3L9)'
  });
  
  // Hoya notes validation - maximum 20 characters for AU, NZ, INT regions
  extend('hoyaMaxLength', {
    validate(value, { maxValue }) {
      return !value || value.length <= maxValue;
    },
    params: ['maxValue'],
    message: 'The notes field can have a maximum of 20 characters'
  });
  
  // Shamir notes validation - maximum 65536 characters
  extend('shamirMaxLength', {
    validate(value, { maxValue }) {
      return !value || value.length <= maxValue;
    },
    params: ['maxValue'],
    message: 'The notes field can have a maximum of 65536 characters'
  });
  
  // PlasticPlus notes validation - maximum 100 characters
  extend('plasticPlusMaxLength', {
    validate(value, { maxValue }) {
      return !value || value.length <= maxValue;
    },
    params: ['maxValue'],
    message: 'The notes field can have a maximum of 100 characters'
  });

  extend('no_forbidden_name', {
    validate(value) {
      if (!value) return true;
      const forbidden = ['duplicate', 'do not use', 'dont use', "don't use"];
      const normalized = String(value).toLowerCase();
      const term = forbidden.find(t => normalized.includes(t));
      if (!term) return true;
      const idx = normalized.indexOf(term);
      const word = String(value).substring(idx, idx + term.length);
      return `The word '${word}' is not accepted in the {_field_} field.`;
    }
  });

  extend('valid_name', {
    validate(value) {
      if (!value) return true;
      const name = String(value).trim();
      if (/ {2,}/.test(name)) {
        return 'The {_field_} field cannot contain more than one space between names.';
      }
      if (!/^[\p{L}\p{M}'’\- ]+$/u.test(name)) {
        return 'The {_field_} field can only contain letters, apostrophes and hyphens.';
      }
      return true;
    }
  });

  extend('no_forbidden_email', {
    validate(value) {
      if (!value) return true;
      const forbidden = ['duplicate', 'donotcontact', 'noemail', 'fake'];
      const normalized = String(value).toLowerCase();
      const term = forbidden.find(t => normalized.includes(t));
      if (!term) return true;
      const idx = normalized.indexOf(term);
      const word = String(value).substring(idx, idx + term.length);
      return `The word '${word}' is not accepted in the Email address field.`;
    }
  });
};
